import React, { useState } from 'react';
import { 
  Filter, 
  X, 
  Search, 
  Calendar,
  DollarSign,
  Users,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react';

export interface TenantFilterOptions {
  search?: string;
  status?: string[];
  plan?: string[];
  billingStatus?: string[];
  trialExpiring?: boolean;
  overUsage?: boolean;
  revenueRange?: {
    min?: number;
    max?: number;
  };
  dateRange?: {
    start?: string;
    end?: string;
  };
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

interface TenantFiltersProps {
  filters: TenantFilterOptions;
  onFiltersChange: (filters: TenantFilterOptions) => void;
  onClearFilters: () => void;
  isOpen: boolean;
  onToggle: () => void;
}

const statusOptions = [
  { value: 'ACTIVE', label: 'Active', icon: CheckCircle, color: 'text-green-500' },
  { value: 'TRIAL', label: 'Trial', icon: Clock, color: 'text-yellow-500' },
  { value: 'SUSPENDED', label: 'Suspended', icon: XCircle, color: 'text-red-500' },
  { value: 'INACTIVE', label: 'Inactive', icon: AlertTriangle, color: 'text-gray-500' }
];

const planOptions = [
  { value: 'STARTER', label: 'Starter', color: 'bg-blue-100 text-blue-800' },
  { value: 'PROFESSIONAL', label: 'Professional', color: 'bg-purple-100 text-purple-800' },
  { value: 'ENTERPRISE', label: 'Enterprise', color: 'bg-indigo-100 text-indigo-800' }
];

const billingStatusOptions = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'TRIAL', label: 'Trial' },
  { value: 'PAST_DUE', label: 'Past Due' },
  { value: 'CANCELLED', label: 'Cancelled' }
];

const sortOptions = [
  { value: 'createdAt', label: 'Created Date' },
  { value: 'name', label: 'Name' },
  { value: 'lastActivityAt', label: 'Last Activity' },
  { value: 'billing.totalRevenue', label: 'Revenue' },
  { value: 'usage.users.current', label: 'User Count' }
];

export const TenantFilters: React.FC<TenantFiltersProps> = ({
  filters,
  onFiltersChange,
  onClearFilters,
  isOpen,
  onToggle
}) => {
  const [localFilters, setLocalFilters] = useState<TenantFilterOptions>(filters);

  const handleFilterChange = (key: keyof TenantFilterOptions, value: any) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleArrayFilterChange = (key: keyof TenantFilterOptions, value: string, checked: boolean) => {
    const currentArray = (localFilters[key] as string[]) || [];
    const newArray = checked 
      ? [...currentArray, value]
      : currentArray.filter(item => item !== value);
    
    handleFilterChange(key, newArray.length > 0 ? newArray : undefined);
  };

  const handleRangeFilterChange = (key: keyof TenantFilterOptions, rangeKey: string, value: string) => {
    const currentRange = (localFilters[key] as any) || {};
    const newRange = { ...currentRange, [rangeKey]: value ? parseFloat(value) : undefined };
    
    // Remove empty range
    if (!newRange.min && !newRange.max) {
      handleFilterChange(key, undefined);
    } else {
      handleFilterChange(key, newRange);
    }
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.status?.length) count++;
    if (filters.plan?.length) count++;
    if (filters.billingStatus?.length) count++;
    if (filters.trialExpiring) count++;
    if (filters.overUsage) count++;
    if (filters.revenueRange?.min || filters.revenueRange?.max) count++;
    if (filters.dateRange?.start || filters.dateRange?.end) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <div className="bg-white border-b">
      {/* Filter Toggle Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <button
            onClick={onToggle}
            className={`flex items-center px-4 py-2 rounded-lg border transition-colors ${
              isOpen 
                ? 'bg-primary-50 border-primary-200 text-primary-700' 
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-2 px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full">
                {activeFilterCount}
              </span>
            )}
          </button>

          {activeFilterCount > 0 && (
            <button
              onClick={onClearFilters}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Clear all filters
            </button>
          )}
        </div>

        {/* Search Bar */}
        <div className="mt-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search firms by name or subdomain..."
              value={filters.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value || undefined)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      {isOpen && (
        <div className="border-t border-gray-200 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Status</label>
                <div className="space-y-2">
                  {statusOptions.map((option) => {
                    const Icon = option.icon;
                    return (
                      <label key={option.value} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={filters.status?.includes(option.value) || false}
                          onChange={(e) => handleArrayFilterChange('status', option.value, e.target.checked)}
                          className="mr-3 rounded"
                        />
                        <Icon className={`w-4 h-4 mr-2 ${option.color}`} />
                        <span className="text-sm text-gray-700">{option.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Plan Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Plan</label>
                <div className="space-y-2">
                  {planOptions.map((option) => (
                    <label key={option.value} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.plan?.includes(option.value) || false}
                        onChange={(e) => handleArrayFilterChange('plan', option.value, e.target.checked)}
                        className="mr-3 rounded"
                      />
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${option.color} mr-2`}>
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Billing Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Billing Status</label>
                <div className="space-y-2">
                  {billingStatusOptions.map((option) => (
                    <label key={option.value} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.billingStatus?.includes(option.value) || false}
                        onChange={(e) => handleArrayFilterChange('billingStatus', option.value, e.target.checked)}
                        className="mr-3 rounded"
                      />
                      <span className="text-sm text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Sort Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Sort By</label>
                <div className="space-y-3">
                  <select
                    value={filters.sortBy || 'createdAt'}
                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {sortOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="sortOrder"
                        value="asc"
                        checked={filters.sortOrder === 'asc'}
                        onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">Ascending</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="sortOrder"
                        value="desc"
                        checked={filters.sortOrder === 'desc' || !filters.sortOrder}
                        onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">Descending</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Filters Row */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Special Conditions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Special Conditions</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.trialExpiring || false}
                        onChange={(e) => handleFilterChange('trialExpiring', e.target.checked || undefined)}
                        className="mr-3 rounded"
                      />
                      <AlertTriangle className="w-4 h-4 text-yellow-500 mr-2" />
                      <span className="text-sm text-gray-700">Trials expiring soon</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.overUsage || false}
                        onChange={(e) => handleFilterChange('overUsage', e.target.checked || undefined)}
                        className="mr-3 rounded"
                      />
                      <Users className="w-4 h-4 text-red-500 mr-2" />
                      <span className="text-sm text-gray-700">Over usage limits</span>
                    </label>
                  </div>
                </div>

                {/* Revenue Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    <DollarSign className="w-4 h-4 inline mr-1" />
                    Revenue Range
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.revenueRange?.min || ''}
                      onChange={(e) => handleRangeFilterChange('revenueRange', 'min', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <span className="text-gray-500">to</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.revenueRange?.max || ''}
                      onChange={(e) => handleRangeFilterChange('revenueRange', 'max', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Date Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Created Date Range
                  </label>
                  <div className="space-y-2">
                    <input
                      type="date"
                      value={filters.dateRange?.start || ''}
                      onChange={(e) => handleRangeFilterChange('dateRange', 'start', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <input
                      type="date"
                      value={filters.dateRange?.end || ''}
                      onChange={(e) => handleRangeFilterChange('dateRange', 'end', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Active Filters Display */}
            {activeFilterCount > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-700">Active Filters:</h4>
                  <button
                    onClick={onClearFilters}
                    className="text-sm text-primary-600 hover:text-primary-800"
                  >
                    Clear All
                  </button>
                </div>
                
                <div className="mt-2 flex flex-wrap gap-2">
                  {filters.search && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      Search: "{filters.search}"
                      <button
                        onClick={() => handleFilterChange('search', undefined)}
                        className="ml-2 text-primary-600 hover:text-primary-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  
                  {filters.status?.map((status) => (
                    <span key={status} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Status: {status}
                      <button
                        onClick={() => handleArrayFilterChange('status', status, false)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  
                  {filters.plan?.map((plan) => (
                    <span key={plan} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      Plan: {plan}
                      <button
                        onClick={() => handleArrayFilterChange('plan', plan, false)}
                        className="ml-2 text-purple-600 hover:text-purple-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  
                  {filters.trialExpiring && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      Trials Expiring
                      <button
                        onClick={() => handleFilterChange('trialExpiring', undefined)}
                        className="ml-2 text-yellow-600 hover:text-yellow-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  
                  {filters.overUsage && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Over Usage
                      <button
                        onClick={() => handleFilterChange('overUsage', undefined)}
                        className="ml-2 text-red-600 hover:text-red-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
